import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Box, AspectRatio, BoxProps, Stack, Text } from "@chakra-ui/react";

import { getPhotoSrc } from "utils/offer";
import { IPhoto, IVideo } from "utils/offer/types";
import { createContext } from "helpers/createContext";
import useGalleryModal from "hooks/useGalleryModal";
import useSwiper, { SwiperProviderActionProps } from "./hooks/useSwiper";
import CustomImage from "../CustomImage";
import IconPlay from "../Icons/Play";
import IconVirtualView from "../Icons/VirtualView";
import CustomLink from "../CustomLink";
import GalleryMiniatures from "./GalleryMiniatures";
import { isPhotoType } from "src/types/types";

const MediaControl: React.FC<{ side: "left" | "right" }> = ({ side }) => (
  <Stack
    align="center"
    justify="center"
    bg="gray.50"
    boxSize="22px"
    borderRadius="100px"
    _hover={{ cursor: "pointer" }}
  >
    {side === "left" ? (
      <ChevronLeftIcon boxSize="20px" ml="-1px" />
    ) : (
      <ChevronRightIcon boxSize="20px" mr="-1px" />
    )}
  </Stack>
);

type IView360 = {
  url: string;
};

const Matterport360: React.FC<IView360> = ({ url }) => (
  <CustomLink
    href={url}
    target="_blank"
    position="absolute"
    cursor="pointer"
    zIndex={999}
    bottom={4}
    right={4}
    w="50px"
    h="50px"
    borderRadius="6px"
    borderColor="ud-primary"
    borderWidth="1px"
    backgroundColor="ud-white"
    textAlign="center"
  >
    <IconVirtualView boxSize="18px" />
    <Text color="ud-primary" fontWeight="bold" fontSize="12px">
      360°
    </Text>
  </CustomLink>
);

const [MediaProviderAction, useMedia] =
  createContext<SwiperProviderActionProps>({
    name: "MediaGallery",
  });

export { useMedia };

type MediaGalleryProps = {
  media?: Array<IPhoto | IVideo>;
  introPhoto?: IPhoto;
  isVideo?: boolean;
  leftTopElement?: React.ReactNode;
  leftBottomElement?: React.ReactNode;
  rightTopElement?: React.ReactNode;
  rightBottomElement?: React.ReactNode;
  streetViewButton?: [number, number] | false; // [lat, lng]
  isDetail?: boolean;
  imageErrorFallback?: string;
  isHoverEffect?: boolean;
  matterportURl?: string;
} & BoxProps;

const MediaGallery: React.FC<MediaGalleryProps> = ({
  media = [],
  isVideo = false,
  introPhoto = {
    id: 1,
    path: "/fe/img/common/inzerat_bez_fotky.jpg",
  },
  leftTopElement,
  leftBottomElement,
  rightTopElement,
  rightBottomElement,
  streetViewButton,
  isDetail,
  imageErrorFallback = "/fe/img/common/inzerat_bez_fotky.jpg",
  isHoverEffect = false,
  matterportURl,
  ...restBoxProps
}) => {
  const { renderGalleryModal, setGalleryModalOpen } = useGalleryModal({
    media,
    introPhoto,
  });

  const { renderSwiper, swiperActions } = useSwiper({
    media,
    imageErrorFallback,
    mediaStyles: { cursor: "pointer" },
    photoOnClick: () => {
      if (isDetail) {
        setGalleryModalOpen(true, 0);
      }
    },
    isOpenModalGallery: true,
    isHoverEffect,
  });

  const showSwitchPhotosButtons = React.useMemo(
    () => !isDetail && media.length > 1,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [media.length],
  );

  const firstMedia = isPhotoType(media[0]) ? media[0] : introPhoto;

  return (
    <>
      {renderGalleryModal()}

      <MediaProviderAction value={swiperActions}>
        <Box {...restBoxProps} position="relative">
          {matterportURl && <Matterport360 url={matterportURl} />}

          {(leftBottomElement || isDetail) && (
            <Box position="absolute" zIndex={50} bottom={4} left={4}>
              {isDetail && (
                <GalleryMiniatures
                  imageErrorFallback={imageErrorFallback}
                  media={media}
                  introPhoto={introPhoto}
                  onClick={(newIndex) => {
                    setGalleryModalOpen(true, newIndex);
                  }}
                />
              )}
              {leftBottomElement}
            </Box>
          )}

          {leftTopElement && (
            <Box position="absolute" zIndex={999} top={4} left={4}>
              {leftTopElement}
            </Box>
          )}

          {isVideo && (
            <Box
              cursor="pointer"
              position="absolute"
              zIndex={999}
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)"
              backgroundColor="pink"
              borderRadius="6px"
              w="80px"
              h="80px"
              onClick={() => {
                setGalleryModalOpen(true, 0);
              }}
            >
              <IconPlay
                fill="ud-white"
                boxSize="48px"
                display="flex"
                margin="auto"
                h="100%"
              />
            </Box>
          )}

          {(rightBottomElement || streetViewButton) && (
            <Box position="absolute" zIndex={999} bottom={4} right={4}>
              {rightBottomElement}
            </Box>
          )}

          {rightTopElement && (
            <Box
              position="absolute"
              zIndex={999}
              top={4}
              right={4}
              userSelect="none"
              unselectable="off"
            >
              {rightTopElement}
            </Box>
          )}

          {showSwitchPhotosButtons && (
            <>
              <Box
                as="button"
                aria-label="move to right"
                zIndex={999}
                position="absolute"
                top="50%"
                right={4}
                onClick={() => {
                  swiperActions.goNext();
                }}
                userSelect="none"
                unselectable="off"
              >
                <MediaControl side="right" />
              </Box>

              <Box
                as="button"
                aria-label="move to left"
                zIndex={999}
                position="absolute"
                top="50%"
                left={4}
                onClick={() => {
                  swiperActions.goBack();
                }}
              >
                <MediaControl side="left" />
              </Box>
            </>
          )}
          {!isDetail
            ? renderSwiper
            : media.length > 0 && (
                <AspectRatio
                  maxW="100%"
                  width="100%"
                  maxH="100%"
                  height="100%"
                  ratio={4 / 3}
                  position="relative"
                  cursor="pointer"
                  as="button"
                  onClick={() => {
                    setGalleryModalOpen(true, 0);
                  }}
                >
                  <CustomImage
                    quality={100}
                    src={getPhotoSrc(firstMedia, imageErrorFallback)}
                    alt={firstMedia?.alt || ""}
                    fill
                    style={{
                      objectFit: "cover",
                    }}
                  />
                </AspectRatio>
              )}
        </Box>
      </MediaProviderAction>
    </>
  );
};

export default MediaGallery;
